// POS-I215 / POS-I216: contrast of a tenant's brand colour against the sidebar background.
//
// The sidebar uses `.glass` = bg-card at 70% over --background (index.css). The HSL tokens below are
// copied from the :root and .dark blocks of index.css; brand-contrast.spec.ts reads index.css and fails
// if they drift. The text of the store name is the only place the colour is used on a coloured ground.

export type SidebarMode = 'light' | 'dark';

/** "H S% L%" tokens exactly as written in index.css (--card, --background). */
export const SIDEBAR_TOKENS: Record<SidebarMode, { card: string; background: string }> = {
  light: { card: '0 0% 100%', background: '220 20% 97%' },
  dark: { card: '224 50% 8%', background: '224 71% 4%' },
};

/** The `.glass` class: bg-card/70. */
const GLASS_ALPHA = 0.7;

/** Smallest ratio WCAG asks for large or bold text; the store name is bold. */
export const MIN_BRAND_CONTRAST = 3;

type Rgb = [number, number, number];

export function parseHex(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function isHexColor(value: string): boolean {
  return /^#[0-9a-f]{6}$/i.test(value.trim());
}

export function hslTokenToRgb(token: string): Rgb {
  const [hRaw, sRaw, lRaw] = token.split(/\s+/);
  const h = parseFloat(hRaw);
  const s = parseFloat(sRaw) / 100;
  const l = parseFloat(lRaw) / 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    return (l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))) * 255;
  };
  return [f(0), f(8), f(4)];
}

/** Effective sidebar background (card at 70% over the page background) as RGB. */
export function sidebarBackground(mode: SidebarMode): Rgb {
  const card = hslTokenToRgb(SIDEBAR_TOKENS[mode].card);
  const page = hslTokenToRgb(SIDEBAR_TOKENS[mode].background);
  return [0, 1, 2].map((i) => card[i] * GLASS_ALPHA + page[i] * (1 - GLASS_ALPHA)) as Rgb;
}

/** The sidebar background as #rrggbb, for the preview boxes in Settings. */
export function sidebarBackgroundHex(mode: SidebarMode): string {
  return '#' + sidebarBackground(mode).map((c) => Math.round(c).toString(16).padStart(2, '0')).join('');
}

function luminance([r, g, b]: Rgb): number {
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio between two RGB colours (1 to 21). */
export function contrastBetween(a: Rgb, b: Rgb): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Contrast of a hex colour against a hex colour; null when either is not a #rrggbb value. */
export function contrastRatio(hexA: string, hexB: string): number | null {
  const a = parseHex(hexA);
  const b = parseHex(hexB);
  return a && b ? contrastBetween(a, b) : null;
}

/** Contrast of a brand colour on the sidebar of one theme; null for an invalid colour. */
export function brandContrastOn(hex: string, mode: SidebarMode): number | null {
  const c = parseHex(hex);
  return c ? contrastBetween(c, sidebarBackground(mode)) : null;
}

/** The modes (light, dark) in which a colour falls under the minimum ratio. */
export function weakModes(hex: string): SidebarMode[] {
  return (['light', 'dark'] as const).filter((m) => {
    const r = brandContrastOn(hex, m);
    return r !== null && r < MIN_BRAND_CONTRAST;
  });
}

/**
 * The colour to use for the store name in the sidebar: the saved brand colour when it reads on the
 * current background, otherwise undefined (the caller keeps the normal text colour). Also undefined
 * for an empty or invalid colour, so the default gradient stays.
 */
export function readableBrandColor(hex: string | null | undefined, mode: SidebarMode): string | undefined {
  if (!hex) return undefined;
  const r = brandContrastOn(hex, mode);
  if (r === null) return undefined;
  return r >= MIN_BRAND_CONTRAST ? hex : undefined;
}

/** Preset swatches offered in Settings; each reaches MIN_BRAND_CONTRAST on both sidebars (spec). */
export const BRAND_PRESETS: readonly string[] = [
  '#2563eb',
  '#7c3aed',
  '#db2777',
  '#dc2626',
  '#d97706',
  '#059669',
];
